import {
  ButtonGroup,
  ButtonV2,
  Drawer,
  Loading,
} from '@moneyforward/ap-frontend-components';

import { TransactionDetailOptionForm } from '@/features/transaction_items/Detail/components/Drawer/Form/TransactionDetailOptionForm';
import { useConfirmCancelEditOption } from '@/features/transaction_items/Detail/hooks/useConfirmCancelEditOption';
import {
  TransactionDetailOptionDrawerMode,
  TransactionDetailOptionFormType,
} from '@/features/transaction_items/Detail/type';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import classNames from 'classnames';
import { FC, memo, useCallback } from 'react';
import { UseFormReturn, useFormState } from 'react-hook-form';
import styles from './TransactionDetailOptionDrawer.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  mode: TransactionDetailOptionDrawerMode;
  visible: boolean;
  onClose: () => void;
  onSave: (data: TransactionDetailOptionFormType) => void;
  methods: UseFormReturn<TransactionDetailOptionFormType>;
};

export const TransactionDetailOptionDrawer: FC<Props> = memo(
  ({ mode, visible, onClose, onSave, methods }: Props) => {
    const { t } = useTranslation();
    const { control } = methods;
    const { isSubmitting, isDirty } = useFormState({ control });

    const { confirm, ConfirmModal } = useConfirmCancelEditOption();
    const containerRef = useGlobalContainerRef();

    const onCloseDrawer = useCallback(async () => {
      if (mode === TransactionDetailOptionDrawerMode.EDIT_MODE && isDirty) {
        const isConfirmed = await confirm();
        if (isConfirmed) {
          onClose();
        }
      } else {
        onClose();
      }
    }, [onClose, mode, confirm, isDirty]);

    return (
      <>
        <Loading
          open={isSubmitting}
          size='xlarge'
          color='white'
          isDialogMode
          getContainer={containerRef}
        />
        <Drawer
          title={t(`transaction_items.detail.options.drawer.${mode}.title`)}
          mask
          maskClosable={false}
          footer={
            <ButtonGroup direction='horizontal'>
              <ButtonV2
                isTertiary
                onClick={onCloseDrawer}
                isDisabled={isSubmitting}
              >
                {t('cancel')}
              </ButtonV2>
              <ButtonV2
                type='submit'
                form='transaction_detail_option_form'
                isDisabled={isSubmitting}
              >
                {mode === TransactionDetailOptionDrawerMode.ADD_MODE
                  ? t('add')
                  : t('save')}
              </ButtonV2>
            </ButtonGroup>
          }
          visible={visible}
          onClose={onCloseDrawer}
        >
          <div className={cx(styles['container'])}>
            <TransactionDetailOptionForm methods={methods} onSubmit={onSave} />
          </div>
        </Drawer>
        <ConfirmModal />
      </>
    );
  }
);
TransactionDetailOptionDrawer.displayName = 'TransactionDetailOptionDrawer';
